import { useMemo, useState } from 'react';
import type { RiskScenario, RiskScenarioCatalogue } from '../types';

interface RiskCatalogueTableProps {
  catalogues: RiskScenarioCatalogue[];
  loading: boolean;
  error: string | null;
  lastImportedAt: string | null;
  lastImportedBy: string | null;
  importing: boolean;
  importError: string | null;
  importResult: string | null;
  /** Omitted when the user may not import; the import button is then hidden. */
  onImport?: () => void;
}

function formatUserRef(value: string | null | undefined): string {
  if (!value) return 'nobody yet';
  return value.replace(/^user:default\//, '');
}

function formatImportedAt(value: string | null): string {
  if (!value) return 'never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function matchesSearch(scenario: RiskScenario, query: string): boolean {
  if (!query) return true;
  const haystack = [
    scenario.code,
    scenario.title,
    scenario.threats.join(' '),
    scenario.vulnerabilities,
    scenario.consequence,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

/**
 * Read-only Risk Scenario Catalogs (Digital Applications / Onsite Systems /
 * Enterprise Systems). This used to be an editable catalogue table
 * (create/edit/archive/delete a Draft risk) — that editing surface was
 * removed entirely: the catalogue's content lives on Confluence, and the
 * only way it changes here is a full re-import via the "Import from
 * Confluence" button below (POST /catalogue/import, see
 * import_risk_catalogue's handler docstring for why this is on-demand and
 * not a scheduled sync). Catalogue tabs and scenario cards are rendered
 * entirely from whatever catalogues/scenarios the last import produced —
 * a new catalogue section or a new risk code on the Confluence page
 * appears here automatically on the next import, nothing about the tab
 * set is hardcoded.
 */
export function RiskCatalogueTable({
  catalogues,
  loading,
  error,
  lastImportedAt,
  lastImportedBy,
  importing,
  importError,
  importResult,
  onImport,
}: RiskCatalogueTableProps) {
  const [activeCatalogueKey, setActiveCatalogueKey] = useState<string | null>(
    null,
  );
  const [search, setSearch] = useState('');

  const activeCatalogue =
    catalogues.find(
      catalogue => catalogue.catalogueKey === activeCatalogueKey,
    ) ??
    catalogues[0] ??
    null;

  const visibleScenarios = useMemo(() => {
    if (!activeCatalogue) return [];
    const normalized = search.trim().toLowerCase();
    return activeCatalogue.scenarios.filter(scenario =>
      matchesSearch(scenario, normalized),
    );
  }, [activeCatalogue, search]);

  return (
    <div className="risk-library-card">
      <section
        className="risk-catalogue-intro"
        aria-labelledby="risk-catalogue-title"
      >
        <div>
          <span className="risk-catalogue-eyebrow">
            Security risk scenario catalogs
          </span>
          <h2 id="risk-catalogue-title">Risk Scenario Catalogs</h2>
          <p>
            Use these catalogs as guidance when identifying security risks.
            Select the catalog that matches the system context and adapt the
            scenarios to the assessment scope. The catalogs are not exhaustive
            and are provided as a read-only reference, imported from Confluence.
          </p>
        </div>
        <div className="risk-catalogue-import-panel">
          {onImport && (
            <button
              type="button"
              className="risk-library-button"
              onClick={onImport}
              disabled={importing}
            >
              {importing ? 'Importing…' : 'Import from Confluence'}
            </button>
          )}
          {importError && (
            <span className="risk-catalogue-import-result is-error">
              {importError}
            </span>
          )}
          {!importError && importResult && (
            <span className="risk-catalogue-import-result">{importResult}</span>
          )}
        </div>
      </section>

      {error && <p className="risk-library-error">{error}</p>}

      {loading && <p className="loading-text">Loading catalogues…</p>}

      {!loading && catalogues.length === 0 && !error && (
        <p className="risk-library-muted">
          No Risk Scenario Catalogs have been imported yet. Use "Import from
          Confluence" above to load them.
        </p>
      )}

      {!loading && catalogues.length > 0 && activeCatalogue && (
        <>
          <div
            className="risk-catalogue-tabs"
            role="tablist"
            aria-label="Risk scenario catalogs"
          >
            {catalogues.map(catalogue => (
              <button
                type="button"
                key={catalogue.catalogueKey}
                className={`risk-catalogue-tab${
                  catalogue.catalogueKey === activeCatalogue.catalogueKey
                    ? ' is-active'
                    : ''
                }`}
                role="tab"
                aria-selected={
                  catalogue.catalogueKey === activeCatalogue.catalogueKey
                }
                onClick={() => {
                  setActiveCatalogueKey(catalogue.catalogueKey);
                  setSearch('');
                }}
              >
                <span className="risk-catalogue-tab-title">
                  {catalogue.catalogueTitle.replace(
                    /^security risk scenario catalog for\s+/i,
                    '',
                  )}
                </span>
                <span className="risk-catalogue-tab-meta">
                  {catalogue.scenarios.length} reference scenarios
                </span>
              </button>
            ))}
          </div>

          <section
            className="risk-catalogue-panel"
            aria-labelledby="catalog-title"
          >
            <div className="risk-catalogue-panel-heading">
              <div>
                <span className="risk-catalogue-section-label">
                  {activeCatalogue.catalogueApplicability}
                </span>
                <h2 id="catalog-title">{activeCatalogue.catalogueTitle}</h2>
              </div>
              <span className="risk-catalogue-count">
                {visibleScenarios.length} of {activeCatalogue.scenarios.length}{' '}
                scenarios
              </span>
            </div>

            <div className="risk-catalogue-toolbar">
              <input
                className="risk-library-input risk-catalogue-search"
                type="search"
                value={search}
                placeholder="Search by risk ID, scenario, threat, vulnerability or consequence"
                aria-label="Search the selected risk scenario catalog"
                onChange={event => setSearch(event.target.value)}
              />
            </div>

            <div className="risk-scenario-list">
              {visibleScenarios.length === 0 ? (
                <p className="risk-catalogue-empty">
                  No risk scenarios match your search.
                </p>
              ) : (
                visibleScenarios.map(scenario => (
                  <article className="risk-scenario-card" key={scenario.code}>
                    <header className="risk-scenario-card-header">
                      <div className="risk-scenario-heading">
                        <span className="risk-scenario-code">
                          {scenario.code}
                        </span>
                        <h4>{scenario.title}</h4>
                      </div>
                      <small>Reference scenario</small>
                    </header>
                    <div className="risk-scenario-grid">
                      <section className="risk-scenario-column">
                        <h5>Example threats</h5>
                        {scenario.threats.length > 0 ? (
                          <ul>
                            {scenario.threats.map(threat => (
                              <li key={threat}>{threat}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>—</p>
                        )}
                      </section>
                      <section className="risk-scenario-column">
                        <h5>Example vulnerabilities</h5>
                        <p>{scenario.vulnerabilities || '—'}</p>
                      </section>
                      <section className="risk-scenario-column">
                        <h5>Potential consequence</h5>
                        <p>{scenario.consequence || '—'}</p>
                      </section>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </>
      )}

      <p className="risk-catalogue-footer">
        Updated by: {formatUserRef(lastImportedBy)} and last updated:{' '}
        {formatImportedAt(lastImportedAt)}
      </p>
    </div>
  );
}
